// MJOS-132 — Second hero panel: VRoid anime-style 3D avatar.
//
// Engine: @pixiv/three-vrm (VRM 1.0). Character: fem_vroid.vrm (CC0,
// VRoid Studio sample) hot-linked from madjin/vrm-samples.
// Audio + visemes: HeadTTS (Kokoro WebGPU), driven into VRM expression
// presets (aa / ih / ou / ee / oh) instead of TalkingHead's Oculus blend.

import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { VRMLoaderPlugin, type VRM } from "@pixiv/three-vrm";
import { HeadTTS } from "@met4citizen/headtts";
import { Loader2, Volume2 } from "lucide-react";

const VRM_URL =
  "https://raw.githubusercontent.com/madjin/vrm-samples/master/vroid/fem_vroid.vrm";

const HEADTTS_WORKER =
  "https://cdn.jsdelivr.net/npm/@met4citizen/headtts@1.3/modules/worker-tts.mjs";
const HEADTTS_DICT =
  "https://cdn.jsdelivr.net/npm/@met4citizen/headtts@1.3/dictionaries/";

const VISEME_TO_VRM: Record<string, string | null> = {
  aa: "aa",
  E: "ee",
  I: "ih",
  O: "oh",
  U: "ou",
  sil: null,
  PP: null,
  FF: null,
  TH: null,
  DD: null,
  kk: null,
  CH: null,
  SS: null,
  nn: null,
  RR: null,
};

const VRM_OPEN_PRESETS = ["aa", "ee", "ih", "oh", "ou"] as const;

const TEST_SCRIPT =
  "Hi, I'm rendered with three-vrm. My visemes come from the same HeadTTS Kokoro pipeline as the panel above, but I render through pixiv's anime-style VRM engine. Same audio, different look.";

export function VRoidPanel() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const vrmRef = useRef<VRM | null>(null);
  const ttsRef = useRef<HeadTTS | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animFrameRef = useRef<number>(0);

  const [loadState, setLoadState] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");
  const [loadPct, setLoadPct] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [ttsState, setTtsState] = useState<
    "cold" | "warming" | "ready" | "speaking" | "error"
  >("cold");
  const [ttsError, setTtsError] = useState<string | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    let cancelled = false;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1c1917);

    const camera = new THREE.PerspectiveCamera(
      28,
      node.clientWidth / node.clientHeight,
      0.1,
      20,
    );
    camera.position.set(0, 1.4, 1.4);
    camera.lookAt(0, 1.4, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(node.clientWidth, node.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    node.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    scene.add(new THREE.AmbientLight(0xffffff, 1.2));
    const dir = new THREE.DirectionalLight(0xffffff, 1.6);
    dir.position.set(1, 2, 1.5);
    scene.add(dir);
    const rim = new THREE.DirectionalLight(0x9ec8ff, 0.4);
    rim.position.set(-1, 1, -1);
    scene.add(rim);

    const loader = new GLTFLoader();
    loader.register((parser) => new VRMLoaderPlugin(parser));

    setLoadState("loading");
    setLoadPct(0);

    loader.load(
      VRM_URL,
      (gltf) => {
        if (cancelled) return;
        const vrm = (gltf.userData as { vrm?: VRM }).vrm;
        if (!vrm) {
          setLoadError("GLB loaded but no VRM userData attached");
          setLoadState("error");
          return;
        }
        scene.add(vrm.scene);
        vrmRef.current = vrm;

        // fem_vroid.vrm is VRM 0.x (faces -Z). VRM 1.0 faces +Z. Detect via
        // meta.metaVersion ("0" vs "1") and flip 0.x characters so they face
        // the camera. Many community CC0 VRMs are still 0.x.
        const metaVersion = (vrm.meta as { metaVersion?: string } | undefined)
          ?.metaVersion;
        if (metaVersion !== "1") {
          vrm.scene.rotation.y = Math.PI;
        }

        const head = vrm.humanoid?.getNormalizedBoneNode("head");
        if (head) {
          const wp = new THREE.Vector3();
          head.getWorldPosition(wp);
          camera.position.set(wp.x, wp.y + 0.05, wp.z + 0.55);
          camera.lookAt(wp.x, wp.y, wp.z);
        }

        setLoadState("loaded");
      },
      (ev) => {
        if (ev.lengthComputable) {
          setLoadPct(Math.min(100, Math.round((ev.loaded / ev.total) * 100)));
        }
      },
      (err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setLoadError(msg);
        setLoadState("error");
      },
    );

    const clock = new THREE.Clock();
    let blinkTimer = 0;
    let nextBlinkAt = 2 + Math.random() * 4;
    let blinkPhase: "idle" | "closing" | "opening" = "idle";
    let blinkProgress = 0;

    const tick = () => {
      animFrameRef.current = requestAnimationFrame(tick);
      const dt = clock.getDelta();
      const vrm = vrmRef.current;
      if (vrm) {
        blinkTimer += dt;
        if (blinkPhase === "idle" && blinkTimer >= nextBlinkAt) {
          blinkPhase = "closing";
          blinkProgress = 0;
          blinkTimer = 0;
        }
        if (blinkPhase === "closing") {
          blinkProgress += dt * 10;
          vrm.expressionManager?.setValue("blink", Math.min(1, blinkProgress));
          if (blinkProgress >= 1) {
            blinkPhase = "opening";
            blinkProgress = 0;
          }
        } else if (blinkPhase === "opening") {
          blinkProgress += dt * 7;
          vrm.expressionManager?.setValue(
            "blink",
            Math.max(0, 1 - blinkProgress),
          );
          if (blinkProgress >= 1) {
            blinkPhase = "idle";
            vrm.expressionManager?.setValue("blink", 0);
            nextBlinkAt = 2 + Math.random() * 4;
          }
        }
        vrm.update(dt);
      }
      renderer.render(scene, camera);
    };
    tick();

    const onResize = () => {
      if (!rendererRef.current) return;
      camera.aspect = node.clientWidth / node.clientHeight;
      camera.updateProjectionMatrix();
      rendererRef.current.setSize(node.clientWidth, node.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      if (node.contains(renderer.domElement)) {
        node.removeChild(renderer.domElement);
      }
      vrmRef.current = null;
      rendererRef.current = null;
    };
  }, []);

  const warmUpTTS = useCallback(async () => {
    if (ttsRef.current || ttsState === "warming") return;
    setTtsState("warming");
    setTtsError(null);
    try {
      const tts = new HeadTTS({
        endpoints: ["webgpu", "wasm"],
        languages: ["en-us"],
        voices: ["af_bella"],
        defaultVoice: "af_bella",
        defaultLanguage: "en-us",
        workerModule: HEADTTS_WORKER,
        dictionaryURL: HEADTTS_DICT,
      });
      await tts.connect();
      tts.onmessage = () => { /* swallow */ };
      await tts.synthesize({ input: "Ready." });
      ttsRef.current = tts;
      setTtsState("ready");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTtsError(msg);
      setTtsState("error");
    }
  }, [ttsState]);

  const speak = useCallback(async (text: string) => {
    const vrm = vrmRef.current;
    const tts = ttsRef.current;
    if (!vrm || !tts) return;
    setTtsState("speaking");

    const audioCtx = (tts as unknown as { settings: { audioCtx: AudioContext } })
      .settings.audioCtx;
    if (audioCtx.state === "suspended") {
      await audioCtx.resume();
    }
    let nextStartTime = audioCtx.currentTime;
    const baseOffsetMs = performance.now();

    const scheduleVisemes = (
      visemes: string[],
      vtimes: number[],
      vdurations: number[],
      startMs: number,
    ) => {
      for (let i = 0; i < visemes.length; i++) {
        const v = visemes[i];
        const preset = VISEME_TO_VRM[v];
        if (!preset) continue;
        const tStart = vtimes[i];
        const dur = vdurations[i];
        const now = performance.now();
        const delay = Math.max(0, startMs + tStart - now);
        window.setTimeout(() => {
          if (!vrmRef.current) return;
          const exp = vrmRef.current.expressionManager;
          if (!exp) return;
          VRM_OPEN_PRESETS.forEach((p) => exp.setValue(p, 0));
          exp.setValue(preset, 0.85);
          window.setTimeout(() => {
            if (vrmRef.current?.expressionManager) {
              vrmRef.current.expressionManager.setValue(preset, 0);
            }
          }, Math.max(60, dur * 0.8));
        }, delay);
      }
    };

    tts.onmessage = (msg: { data?: SpeechChunk } & Record<string, unknown>) => {
      const d = msg.data;
      if (!d) return;
      if (d.audio && typeof (d.audio as AudioBuffer).duration === "number") {
        const buf = d.audio as AudioBuffer;
        const src = audioCtx.createBufferSource();
        src.buffer = buf;
        src.connect(audioCtx.destination);
        const startAt = Math.max(audioCtx.currentTime + 0.02, nextStartTime);
        src.start(startAt);
        const chunkStartMs = baseOffsetMs + (startAt - audioCtx.currentTime) * 1000;
        nextStartTime = startAt + buf.duration;
        if (d.visemes && d.vtimes && d.vdurations) {
          scheduleVisemes(d.visemes, d.vtimes, d.vdurations, chunkStartMs);
        }
      }
    };

    try {
      await tts.synthesize({ input: text, voice: "af_bella" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTtsError(msg);
    } finally {
      window.setTimeout(() => setTtsState("ready"), text.length * 70 + 1000);
    }
  }, []);

  return (
    <div className="rounded-2xl border bg-white dark:bg-stone-900 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b">
        <div>
          <h2 className="text-base font-semibold">VRoid Anime (three-vrm + Kokoro)</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Stylized 3D anime character · VRM 1.0 viseme presets · same Kokoro audio · MIT
          </p>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wide rounded bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 px-2 py-1">
          MIT · STYLIZED
        </span>
      </div>

      <div className="relative w-full h-[520px] bg-stone-900">
        <div ref={containerRef} className="absolute inset-0" />
        {loadState !== "loaded" ? (
          <div className="absolute inset-0 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm">
            <div className="text-center">
              {loadState === "error" ? (
                <p className="text-xs text-amber-300 font-mono">{loadError}</p>
              ) : (
                <>
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-stone-400" />
                  <p className="text-sm text-stone-300">
                    Loading VRoid {loadPct ? `${loadPct}%` : "…"}
                  </p>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {ttsState === "cold" ? (
            <button
              onClick={warmUpTTS}
              disabled={loadState !== "loaded"}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium disabled:opacity-50"
            >
              <Volume2 className="w-4 h-4" />
              Wake up Kokoro (~80 MB)
            </button>
          ) : ttsState === "warming" ? (
            <button disabled className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600/60 text-white text-sm font-medium cursor-not-allowed">
              <Loader2 className="w-4 h-4 animate-spin" />
              Warming Kokoro…
            </button>
          ) : ttsState === "error" ? (
            <span className="text-xs text-amber-300 font-mono">Kokoro: {ttsError}</span>
          ) : (
            <button
              onClick={() => speak(TEST_SCRIPT)}
              disabled={ttsState === "speaking"}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-purple-700 text-white hover:bg-purple-800 text-sm font-medium disabled:opacity-60"
            >
              {ttsState === "speaking" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
              Speak with Kokoro
            </button>
          )}
        </div>

        <p className="text-xs text-muted-foreground pt-1 leading-relaxed">
          Character: fem_vroid.vrm (CC0, VRoid Studio sample, hot-linked from madjin/vrm-samples). Engine: @pixiv/three-vrm. Lipsync drives VRM 1.0 preset expressions (aa/ih/ou/ee/oh) from HeadTTS Oculus viseme timings.
        </p>
      </div>
    </div>
  );
}

type SpeechChunk = {
  audio?: AudioBuffer;
  words?: string[];
  wtimes?: number[];
  wdurations?: number[];
  visemes?: string[];
  vtimes?: number[];
  vdurations?: number[];
};
